import FileEmptyStateIcon from '@assets/icons/file-empty-state.svg';
import i18n from '@/utils/i18n';
import OrderGuideNavIcon from '@assets/icons/order-guide.svg';
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/button';
import { DisplayAd } from '@/components/ads/display-ad';
import { DisplayAdModule } from '@/contexts/ads-context-types';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { IconText } from '@/components/icon-text';
import {
  OrderGuideCollectionsActionSheet,
  Route,
} from './order-guide-collections-action-sheet';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { SCREEN_PADDING } from '@/styles';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useOrderGuideSettingsContext } from '@/contexts/order-guide-settings-context';

interface Props {
  contentContainerStyle: StyleProp<ViewStyle>;
  displayAd: DisplayAdModule | null;
  navigateToCatalog: () => void;
}

export const EmptyOrderGuideContent: React.FC<Props> = ({
  contentContainerStyle,
  displayAd,
  navigateToCatalog,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { selectedCollection } = useOrderGuideItemsContext();
  const { setSelectedCollectionUUID, selectedCollectionUUID } =
    useOrderGuideSettingsContext();

  const [showListActionSheet, setShowListActionSheet] = useState(false);

  useEffect(() => {
    PerfTiming.end(PerfTimingEvent.FIRST_SCREEN_VISIBLE, {
      screenType: 'empty_order_guide',
    });
  }, []);

  const actionButton = selectedCollection?.isLocked ? (
    <Button
      onPress={() => setShowListActionSheet(true)}
      style={styles.button}
      trackingData={{
        name: 'order_guide.get_started_button',
      }}
    >
      {i18n.t('create_new_order_guide')}
    </Button>
  ) : (
    <Button
      onPress={navigateToCatalog}
      style={styles.button}
      trackingData={{
        name: 'order_guide.get_started_button',
      }}
    >
      {i18n.t('get_started')}
    </Button>
  );

  const EmptyStateText = (
    <IconText
      Icon={OrderGuideNavIcon}
      message={
        selectedCollection?.isLocked
          ? i18n.t('partner_supplier_order_guide_empty_state.locked_message')
          : customOrderGuideEditingEnabled
            ? i18n.t('partner_supplier_order_guide_empty_state.message')
            : i18n.t(
                'partner_supplier_order_guide_empty_state.cant_edit_message',
              )
      }
    />
  );

  return (
    <>
      {displayAd ? (
        <ScrollView
          alwaysBounceVertical={false}
          contentContainerStyle={[contentContainerStyle, styles.scrollContent]}
        >
          <View style={styles.displayAd}>
            <DisplayAd displayAd={displayAd} />
          </View>
          <EmptyStateMinimal
            containerStyle={styles.emptyStateContainer}
            iconProps={{ testID: 'empty-order-guide-icon' }}
            ImageComponent={isSmallLayout ? null : FileEmptyStateIcon}
            text={EmptyStateText}
          >
            {actionButton}
          </EmptyStateMinimal>
        </ScrollView>
      ) : (
        <EmptyStateMinimal
          containerStyle={contentContainerStyle}
          iconProps={{ testID: 'empty-order-guide-icon' }}
          ImageComponent={FileEmptyStateIcon}
          text={EmptyStateText}
        >
          {actionButton}
        </EmptyStateMinimal>
      )}
      {showListActionSheet && selectedCollectionUUID ? (
        <OrderGuideCollectionsActionSheet
          initialRoute={Route.AddCollection}
          onClose={() => setShowListActionSheet(false)}
          onSelect={(value) => {
            setSelectedCollectionUUID(value);
            setShowListActionSheet(false);
          }}
          selectedCollectionUUID={selectedCollectionUUID}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  button: {
    marginHorizontal: SCREEN_PADDING,
    marginTop: SCREEN_PADDING,
  },
  displayAd: {
    marginBottom: 8,
  },
  emptyStateContainer: {
    paddingVertical: 24,
  },
  scrollContent: {
    flexGrow: 1,
  },
});
